/**
 * Session helpers for the admin SPA (FINDING-009).
 *
 * Access tokens live in an httpOnly cookie (`omni_auth_token`) set by Node login.
 * Never persist tokens in localStorage — that breaks XSS isolation and was only
 * re-added as a workaround when login Set-Cookie missed the SPA origin.
 *
 * Tab memory keeps Bearer for same-tab call sites; after reload, APIs use
 * `credentials: 'include'` + the cookie.
 */

const TOKEN_KEY = "auth_token";
const USER_KEY = "auth_user";
const LEGACY_ACCESS_TOKEN_KEY = "nvr.auth.accessToken";
const LEGACY_REFRESH_TOKEN_KEY = "nvr.auth.refreshToken";

/** Tab-scoped token only — never written to web storage. */
let memoryToken = null;

function purgeTokenStorage() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
    localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
  } catch {
    /* ignore quota / private mode */
  }
}

purgeTokenStorage();

export const tokenManager = {
  /**
   * Store user profile (localStorage) and optional tab-scoped token (memory only).
   */
  setToken: (token, user) => {
    purgeTokenStorage();
    memoryToken =
      typeof token === "string" && token.trim().length > 0 ? token.trim() : null;

    if (user != null) {
      try {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
      } catch (error) {
        console.error("Error storing user data:", error);
      }
    }
  },

  /**
   * In-memory token for this tab, or null after reload / logout.
   */
  getToken: () => {
    purgeTokenStorage();
    return memoryToken;
  },

  getUser: () => {
    const userData = localStorage.getItem(USER_KEY);
    if (!userData) return null;

    try {
      return JSON.parse(userData);
    } catch (error) {
      console.error("Error parsing user data:", error);
      return null;
    }
  },

  clearAuth: () => {
    memoryToken = null;
    purgeTokenStorage();
    try {
      localStorage.removeItem(USER_KEY);
    } catch {
      /* ignore */
    }
    try {
      import("../auth/playbackToken.ts").then((m) => m.clearPlaybackTokenCache?.());
    } catch {
      /* ignore */
    }
    if (process.env.NODE_ENV === "development") {
      console.log("Auth data cleared");
    }
  },

  /**
   * Soft UI signal — cookie session confirmed by API with credentials: include.
   */
  isAuthenticated: () => {
    return !!tokenManager.getUser();
  },

  isTokenValid: (token = null) => {
    const authToken = token || tokenManager.getToken();

    if (!authToken || typeof authToken !== "string") {
      // Cookie-only after reload: trust until /api/account/access rejects.
      return !!tokenManager.getUser();
    }

    const parts = authToken.split(".");
    if (parts.length !== 3) {
      return authToken.length > 10;
    }

    try {
      const payload = JSON.parse(atob(parts[1]));

      if (payload.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        if (payload.exp < currentTime) {
          if (process.env.NODE_ENV === "development") {
            console.log("Token expired");
          }
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error("Token validation error:", error);
      return false;
    }
  },
};

export const AUTH_FETCH_CREDENTIALS = "include";
